import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Admin-only, one-time demo sandbox. Nothing runs on startup — this is a manual action.
export const DemoEnvironment = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/admin/demo-environment")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ exists: false, logins: [] }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/create-demo-environment");
      setStatus(data);
      toast.success("Demo Sandbox is ready");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/remove-demo-environment");
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Demo environment removed (${total} record(s))`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <Card className="border-brand-line shadow-none" data-testid="demo-environment-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FlaskConical className="h-4 w-4 text-brand" /> Demo environment
        </CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          A safe "Demo Sandbox" studio with its own Studio Owner and Content Manager logins,
          pre-loaded with a dummy weekly plan, monthly plan and targets. It is tagged as demo and
          is <span className="font-medium">excluded from your HQ dashboards and financial reports</span>,
          so live presenters can click around without touching real numbers. No seed code runs on
          startup — this is a one-time action.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.exists ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="border-0 bg-emerald-50 text-emerald-700" data-testid="demo-active-badge">
                Active · {status.studio?.name}
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-sm" data-testid="demo-logins-table">
                <thead>
                  <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="p-2">Name</th>
                    <th className="p-2">Role</th>
                    <th className="p-2">Mobile</th>
                    <th className="p-2">PIN</th>
                  </tr>
                </thead>
                <tbody>
                  {status.logins.map((l) => (
                    <tr key={l.mobile} className="border-b border-slate-100">
                      <td className="p-2 font-medium text-brand-ink">{l.name}</td>
                      <td className="p-2 text-slate-600">{l.role_label}</td>
                      <td className="p-2 tabular-nums">{l.mobile}</td>
                      <td className="p-2 font-mono text-brand">{l.pin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">
              Both accounts sign in on the <span className="font-medium">Studio login</span> tab
              (mobile + PIN).
            </p>
            <Button
              variant="outline"
              className="min-h-[44px] border-rose-300 text-rose-600 hover:bg-rose-50"
              data-testid="remove-demo-btn"
              disabled={busy}
              onClick={remove}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Remove demo environment
            </Button>
          </>
        ) : (
          <Button
            className="min-h-[44px]"
            data-testid="create-demo-btn"
            disabled={busy}
            onClick={create}
          >
            <FlaskConical className="mr-2 h-4 w-4" />
            {busy ? "Creating…" : "Create demo environment"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
